import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatApiError, API_BASE, asRange } from "@/lib/api";
import { Toaster, toast } from "sonner";
import {
    Download, Share2, MapPin, Loader2, Globe, Lock, Plane, Hotel as HotelIcon, Utensils, Ticket,
} from "lucide-react";

const stopAccent = (i) => (i % 2 === 0 ? "bg-terracotta text-sand" : "bg-jade text-sand");

export default function TripView() {
    const { slug } = useParams();
    const [trip, setTrip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let aborted = false;
        setLoading(true);
        api.get(`/trips/${slug}`)
            .then(({ data }) => !aborted && setTrip(data))
            .catch((e) => !aborted && setError(formatApiError(e.response?.data?.detail) || e.message))
            .finally(() => !aborted && setLoading(false));
        return () => { aborted = true; };
    }, [slug]);

    const share = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({ title: trip?.name || "My Werighty trip", url });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success("Trip link copied to clipboard");
            }
        } catch (_e) {
            // user cancelled
        }
    };

    if (loading) {
        return (
            <div className="App">
                <Navbar />
                <div className="mx-auto mt-24 grid max-w-md place-items-center text-center" data-testid="trip-loading">
                    <Loader2 size={32} className="animate-spin text-terracotta" />
                    <p className="mt-4 font-display text-2xl text-ink">Loading your trip…</p>
                </div>
            </div>
        );
    }
    if (error || !trip) {
        return (
            <div className="App">
                <Navbar />
                <div className="mx-auto mt-24 max-w-xl rounded-3xl border border-destructive/30 bg-destructive/5 p-6 text-center" data-testid="trip-error">
                    <p className="font-display text-2xl text-destructive">{error || "Trip not found"}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="App">
            <Toaster position="top-center" richColors closeButton />
            <Navbar />

            <section className="mx-auto mt-10 max-w-6xl px-6">
                <div className="grid gap-10 md:grid-cols-[1.7fr_1fr] items-end">
                    <div>
                        <span className="overline">your itinerary</span>
                        <h1 data-testid="trip-name" className="mt-2 font-display text-5xl text-ink md:text-7xl">{trip.name}</h1>
                        <p className="mt-4 text-moss">
                            {trip.stops.length} stop{trip.stops.length > 1 ? "s" : ""} · {trip.travelers} traveler{trip.travelers > 1 ? "s" : ""} · {trip.budget_level} budget
                        </p>
                        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                            <span className={`flex items-center gap-1 rounded-full px-3 py-1 ${trip.public ? "bg-jade/10 text-jade" : "bg-ink/10 text-ink"}`}>
                                {trip.public ? <Globe size={12} /> : <Lock size={12} />}
                                {trip.public ? "Public — anyone with this link" : "Private — only you"}
                            </span>
                        </div>
                        {trip.notes && (
                            <p className="mt-6 max-w-2xl rounded-2xl bg-white p-4 text-sm italic text-moss ring-1 ring-jade/10">&ldquo;{trip.notes}&rdquo;</p>
                        )}
                    </div>
                    <div className="glass rounded-3xl p-6 md:p-8" data-testid="trip-total">
                        <p className="overline">total estimated</p>
                        {(() => {
                            const r = asRange(trip.total_estimated_usd);
                            return r.low !== r.high ? (
                                <>
                                    <p className="mt-2 font-display text-4xl text-ink">${r.low.toLocaleString()}<span className="text-moss"> – </span>${r.high.toLocaleString()}</p>
                                    <p className="mt-1 text-xs text-moss">avg <b className="text-terracotta">${r.avg.toLocaleString()}</b> · {trip.travelers} traveler(s), all stops</p>
                                </>
                            ) : (
                                <>
                                    <p className="mt-2 font-display text-5xl text-ink">${r.avg.toLocaleString()}</p>
                                    <p className="mt-1 text-xs text-moss">for {trip.travelers} traveler(s), all stops combined</p>
                                </>
                            );
                        })()}
                        <div className="mt-5 flex flex-wrap gap-2">
                            <a
                                href={`${API_BASE}/trips/${trip.slug}/pdf`}
                                data-testid="trip-pdf"
                                className="btn-primary"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Download size={14} className="mr-2" /> Download PDF
                            </a>
                            {trip.public && (
                                <button onClick={share} data-testid="trip-share" className="btn-secondary">
                                    <Share2 size={14} className="mr-2" /> Share
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Map-style stops summary */}
            <section className="mx-auto mt-12 max-w-6xl px-6">
                <div className="glass rounded-3xl p-6 md:p-8">
                    <p className="overline">your route</p>
                    <ol className="mt-4 flex flex-wrap items-center gap-2" data-testid="trip-route">
                        {trip.stops.map((s, i) => (
                            <li key={i} className="flex items-center gap-2">
                                <span className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm ${stopAccent(i)}`}>
                                    <span className="rounded-full bg-sand/30 px-2 text-xs">{i + 1}</span>
                                    {s.data?.destination?.name || s.destination}
                                    <span className="text-xs opacity-80">· {s.days}d</span>
                                </span>
                                {i < trip.stops.length - 1 && <Plane size={14} className="text-moss" />}
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* Stops detail */}
            <section className="mx-auto mt-12 max-w-6xl space-y-12 px-6">
                {trip.stops.map((stop, i) => {
                    const d = stop.data || {};
                    const cb = d.cost_breakdown_usd || {};
                    return (
                        <article key={i} className="grid gap-8 md:grid-cols-[2fr_1fr]" data-testid={`trip-stop-detail-${i}`}>
                            <div>
                                <span className="overline">stop {i + 1} · {stop.days} day{stop.days > 1 ? "s" : ""}</span>
                                <h2 className="mt-2 font-display text-4xl text-ink md:text-5xl">{d.destination?.name || stop.destination}</h2>
                                {d.destination?.summary && (
                                    <p className="mt-4 max-w-2xl text-moss">{d.destination.summary}</p>
                                )}

                                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                                    {(d.hotels || []).slice(0, 2).map((h, k) => (
                                        <Link
                                            key={k}
                                            to={`/place?destination=${encodeURIComponent(stop.destination)}&type=hotel&name=${encodeURIComponent(h.name)}`}
                                            state={{ item: h }}
                                            className="group block rounded-3xl bg-white p-5 ring-1 ring-jade/10 transition hover:-translate-y-1 hover:shadow-lg"
                                        >
                                            <HotelIcon size={16} className="text-terracotta" />
                                            <h4 className="mt-2 font-display text-xl text-ink group-hover:text-terracotta transition">{h.name}</h4>
                                            <p className="text-xs text-moss">{h.neighborhood} · ${h.price_per_night_usd}/night · ★ {h.rating}</p>
                                        </Link>
                                    ))}
                                    {(d.restaurants || []).slice(0, 1).map((r, k) => (
                                        <Link
                                            key={k}
                                            to={`/place?destination=${encodeURIComponent(stop.destination)}&type=restaurant&name=${encodeURIComponent(r.name)}`}
                                            state={{ item: r }}
                                            className="group block rounded-3xl bg-white p-5 ring-1 ring-jade/10 transition hover:-translate-y-1 hover:shadow-lg"
                                        >
                                            <Utensils size={16} className="text-jade" />
                                            <h4 className="mt-2 font-display text-xl text-ink group-hover:text-terracotta transition">{r.name}</h4>
                                            <p className="text-xs text-moss">{r.cuisine} · {r.price_range} · {r.must_try}</p>
                                        </Link>
                                    ))}
                                    {(d.activities || []).slice(0, 1).map((a, k) => (
                                        <Link
                                            key={k}
                                            to={`/place?destination=${encodeURIComponent(stop.destination)}&type=activity&name=${encodeURIComponent(a.name)}`}
                                            state={{ item: a }}
                                            className="group block rounded-3xl bg-white p-5 ring-1 ring-jade/10 transition hover:-translate-y-1 hover:shadow-lg"
                                        >
                                            <Ticket size={16} className="text-ink" />
                                            <h4 className="mt-2 font-display text-xl text-ink group-hover:text-terracotta transition">{a.name}</h4>
                                            <p className="text-xs text-moss">{a.category} · {a.duration_hours}h · ${a.price_usd}</p>
                                        </Link>
                                    ))}
                                </div>

                                <Link
                                    to={`/explore?destination=${encodeURIComponent(stop.destination)}&days=${stop.days}&travelers=${trip.travelers}&budget_level=${trip.budget_level}`}
                                    className="mt-6 inline-flex items-center gap-2 text-sm text-jade hover:text-terracotta"
                                >
                                    <MapPin size={14} /> See all hotels, restaurants & activities →
                                </Link>
                            </div>

                            <aside className="rounded-3xl bg-white p-6 ring-1 ring-jade/10 md:sticky md:top-24 self-start">
                                <p className="overline">stop budget</p>
                                <ul className="mt-3 text-sm">
                                    {[
                                        ["Flights / person", cb.flights_per_person],
                                        ["Hotel / night", cb.hotel_per_night],
                                        ["Food / day / pax", cb.food_per_day_per_person],
                                        ["Activities / day / pax", cb.activities_per_day_per_person],
                                        ["Transport / day", cb.transport_per_day],
                                    ].map(([label, val], k) => {
                                        const r = asRange(val);
                                        return (
                                            <li key={k} className="flex justify-between border-b border-jade/10 py-2 last:border-0">
                                                <span className="text-moss">{label}</span>
                                                <b>{r.low === r.high ? `$${r.low}` : `$${r.low}–$${r.high}`}</b>
                                            </li>
                                        );
                                    })}
                                </ul>
                                {(() => {
                                    const r = asRange(cb.total_estimated);
                                    return (
                                        <div className="mt-4 rounded-2xl bg-terracotta/10 px-4 py-3">
                                            <span className="text-xs uppercase tracking-[0.18em] text-terracotta">Estimated stop cost</span>
                                            <p className="mt-1 font-display text-2xl text-terracotta">
                                                {r.low === r.high ? `$${r.avg.toLocaleString()}` : `$${r.low.toLocaleString()}–$${r.high.toLocaleString()}`}
                                            </p>
                                        </div>
                                    );
                                })()}
                            </aside>
                        </article>
                    );
                })}
            </section>

            <Footer />
        </div>
    );
}
